/**
 * Permissions Query Keys Factory
 *
 * SINGLE SOURCE OF TRUTH for all permissions-related query keys.
 *
 * This factory ensures consistent cache management across:
 * - Runtime permission checking (sidebar, routes)
 * - Admin permission configuration (roles page)
 * - Project-level permissions
 *
 * USAGE:
 * ```typescript
 * // In queries
 * useQuery({
 *   queryKey: permissionsKeys.matrix.byCompany(companyId),
 *   // ...
 * })
 *
 * // In mutations (invalidation)
 * onSuccess: () => {
 *   queryClient.invalidateQueries({
 *     queryKey: permissionsKeys.matrix.all()
 *   })
 * }
 * ```
 *
 * KEY STRUCTURE:
 * - ['permissions'] - Base key for all permissions
 * - ['permissions', 'me'] - Current user's permissions (runtime)
 * - ['permissions', 'matrix'] - Admin matrix queries
 * - ['permissions', 'matrix', 'company', id] - Company-level matrix
 * - ['permissions', 'matrix', 'project', id] - Project-level matrix
 * - ['permissions', 'inheritance', ...] - Inheritance comparison queries
 */

// ============================================================================
// TYPES
// ============================================================================

export type PermissionLevel = 'global' | 'company' | 'project'

// ============================================================================
// QUERY KEYS FACTORY
// ============================================================================

export const permissionsKeys = {
  /**
   * Base key for all permissions queries.
   * Use for broad invalidation.
   */
  all: () => ['permissions'] as const,

  // ─────────────────────────────────────────────────────────────────────────
  // RUNTIME PERMISSIONS (for sidebar, routes, feature access)
  // ─────────────────────────────────────────────────────────────────────────

  /**
   * Current user's effective permissions.
   * Used by: useSidebarPermissions, ProtectedRoute
   * Endpoint: GET /auth/permissions/me/
   *
   * NOTE: When a project is selected, usePermissions adds ['project', projectId]
   * to this key. Use meWithProject() or invalidate with me() as prefix.
   */
  me: () => [...permissionsKeys.all(), 'me'] as const,

  /**
   * Current user's effective permissions for a specific project.
   * Endpoint: GET /auth/permissions/me/?project={id}
   *
   * @param projectId - Project ID
   */
  meWithProject: (projectId: string) =>
    [...permissionsKeys.me(), 'project', projectId] as const,

  // ─────────────────────────────────────────────────────────────────────────
  // ADMIN MATRIX (for permission configuration pages)
  // ─────────────────────────────────────────────────────────────────────────

  matrix: {
    /**
     * Base key for all matrix queries.
     * Use for invalidating all matrix data.
     */
    all: () => [...permissionsKeys.all(), 'matrix'] as const,

    /**
     * Global permissions matrix (no company filter).
     * Endpoint: GET /auth/permissions/matrix/
     */
    global: () => [...permissionsKeys.matrix.all(), 'global'] as const,

    /**
     * Company-specific permissions matrix.
     * Endpoint: GET /auth/permissions/matrix/?company={id}
     *
     * @param companyId - Company ID or null for global
     */
    byCompany: (companyId: string | null) =>
      companyId
        ? ([...permissionsKeys.matrix.all(), 'company', companyId] as const)
        : permissionsKeys.matrix.global(),

    /**
     * Project-specific permissions matrix.
     * Endpoint: GET /projects/project-permissions/matrix/?project={id}
     *
     * @param projectId - Project ID
     */
    byProject: (projectId: string) =>
      [...permissionsKeys.matrix.all(), 'project', projectId] as const,

    /**
     * Matrix by context (unified key for any level).
     * Use when you need a key that works for any level.
     *
     * @param level - Permission level
     * @param contextId - Company ID, Project ID, or null for global
     */
    byContext: (level: PermissionLevel, contextId: string | null) => {
      switch (level) {
        case 'project':
          return contextId
            ? permissionsKeys.matrix.byProject(contextId)
            : permissionsKeys.matrix.global()
        case 'company':
          return permissionsKeys.matrix.byCompany(contextId)
        default:
          return permissionsKeys.matrix.global()
      }
    },
  },

  // ─────────────────────────────────────────────────────────────────────────
  // INHERITANCE (for comparing levels)
  // ─────────────────────────────────────────────────────────────────────────

  inheritance: {
    /**
     * Base key for inheritance queries.
     */
    all: () => [...permissionsKeys.all(), 'inheritance'] as const,

    /**
     * Inheritance comparison for a specific context.
     *
     * @param role - Role code
     * @param level - Current level being configured
     * @param companyId - Company ID (for company/project levels)
     * @param projectId - Project ID (for project level)
     */
    byContext: (
      role: string,
      level: PermissionLevel,
      companyId: string | null,
      projectId: string | null
    ) =>
      [
        ...permissionsKeys.inheritance.all(),
        { role, level, companyId, projectId },
      ] as const,
  },

  // ─────────────────────────────────────────────────────────────────────────
  // MODULES (available modules/actions)
  // ─────────────────────────────────────────────────────────────────────────

  modules: {
    /**
     * Available modules and actions from backend.
     * Rarely changes, can have long staleTime.
     */
    all: () => [...permissionsKeys.all(), 'modules'] as const,
  },

  // ─────────────────────────────────────────────────────────────────────────
  // PROJECT PERMISSIONS (project-level permission management)
  // ─────────────────────────────────────────────────────────────────────────

  project: {
    /**
     * Base key for all project permissions queries.
     */
    all: () => ['project-permissions'] as const,

    /**
     * Project permissions list (filtered by params).
     * Endpoint: GET /projects/project-permissions/?project={id}
     */
    list: (params?: { project?: string; role?: string; module?: string }) =>
      [...permissionsKeys.project.all(), params] as const,

    /**
     * Project permissions matrix.
     * Endpoint: GET /projects/{id}/permissions/matrix/
     */
    matrix: (projectId: string) =>
      [...permissionsKeys.project.all(), 'matrix', projectId] as const,

    /**
     * User's role and permissions in a specific project.
     * Endpoint: GET /projects/{id}/my-role/
     */
    userRole: (projectId: string) =>
      ['user-project-role', projectId] as const,
  },
} as const

// ============================================================================
// INVALIDATION HELPERS
// ============================================================================

/**
 * Get all keys that should be invalidated after a permission change.
 *
 * @param level - The level that was changed
 * @param contextId - The context ID that was changed (companyId for company level, projectId for project level)
 */
export function getInvalidationKeys(
  level: PermissionLevel,
  contextId: string | null
) {
  const keys: readonly (readonly string[])[] = [
    // Always invalidate the user's own permissions (affects sidebar/routes)
    permissionsKeys.me(),

    // Invalidate the specific matrix that was changed
    permissionsKeys.matrix.byContext(level, contextId),
  ]

  // If we changed project-level, also invalidate project-specific caches
  if (level === 'project' && contextId) {
    return [
      ...keys,
      // Invalidate project-scoped user permissions (sidebar uses this when project selected)
      permissionsKeys.meWithProject(contextId),
      permissionsKeys.project.matrix(contextId),
      permissionsKeys.project.list({ project: contextId }),
      permissionsKeys.project.userRole(contextId),
    ]
  }

  // If we changed company-level, also invalidate all project matrices for that company
  // (since projects inherit from company)
  if (level === 'company') {
    return [
      ...keys,
      permissionsKeys.matrix.all(),
    ]
  }

  // If we changed global, invalidate everything
  if (level === 'global') {
    return [
      ...keys,
      permissionsKeys.all(),
    ]
  }

  return keys
}

// ============================================================================
// TYPE EXPORTS
// ============================================================================

export type PermissionsQueryKeys = typeof permissionsKeys
